'use client';

import UserBlockIconSvg from '@/assets/svgs/main/suppression-lists.svg';
import PlusIconSvg from '@/assets/svgs/plus.svg';
import SearchIconSvg from '@/assets/svgs/search.svg';
import CloseIconSvg from '@/assets/svgs/x-close.svg';
import {
  useFetchContactLists,
  useFetchPendingContactLists,
} from '@/containers/ContactLists/api';
import { selectShouldRefreshList } from '@/containers/ContactLists/redux/contactLists.selectors';
import { setShouldRefreshList } from '@/containers/ContactLists/redux/contactLists.slice';
import { ContactList } from '@/containers/ContactLists/types';
import { selectAssignedSuppressionLists } from '@/containers/Jobs/redux/jobEditors.selectors';
import {
  setAssignSuppressionList,
  setUnassignSuppressionList,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { JobSuppressionListDetails } from '@/containers/Jobs/types';
import { Modals } from '@/containers/Modals/all-modals';
import { useAppDispatch } from '@/lib/redux/store';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { InputContainer } from '@/shared/components/FormInputs';
import useDebounce from '@/shared/hooks/useDebounce';
import { ContactListTypeMapping } from '@/shared/utils/contact-list';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { addOpenModal } from '../redux/modals.slice';
import { AssignSuppressionListsModalParams, ModalProps, P2PUploadParams } from '../types';
import useModals from '../useModals';
import SuppressionListItemView from './SuppressionListItemView';

interface AssignSuppressionListsModalProps extends ModalProps {
  className?: string;
  params?: AssignSuppressionListsModalParams;
}

const modalName = Modals.AssignSuppressionListsModal;

// Helper function to map ContactList to JobSuppressionListDetails
const mapContactListToJobSuppressionList = (
  contactList: ContactList,
  assigned: boolean
): JobSuppressionListDetails & { leads_remaining?: number } => ({
  assigned,
  leads_loaded: contactList.leads_loaded,
  suppression_list_id: contactList.list_id,
  suppression_list_name: contactList.list_name,
  suppression_list_state: contactList.list_state,
  leads_remaining: contactList.leads_remaining,
});

const AssignSuppressionListsModal = ({
  className,
  params,
  variant = 'default',
}: AssignSuppressionListsModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Modal Specific details
  const dispatch = useAppDispatch();
  const jobId = params?.jobId || '';
  const identityId = params?.identityId;
  const {
    isLoading: isLoadingContactLists,
    data: contactLists,
    refetch: refetchContactLists,
  } = useFetchContactLists(identityId, !!identityId);

  const suppressionLists = useMemo(
    () =>
      contactLists?.data?.filter(
        (list) => list.suppress_cell_phones === ContactListTypeMapping.SuppressionList
      ),
    [contactLists?.data]
  );

  const {
    isLoading: isLoadingPendingContactLists,
    data: pendingContactLists,
    refetch: refetchPendingContactLists,
  } = useFetchPendingContactLists(identityId, !!identityId);

  const pendingSuppressionLists = pendingContactLists?.data
    ?.filter(
      (list) => list.suppress_cell_phones === ContactListTypeMapping.SuppressionList
    )
    ?.map((contactList) => mapContactListToJobSuppressionList(contactList, false));
  const [isPendingListChanged, setIsPendingListChanged] = useState(false);
  const numberOfPendingItems = useMemo(
    () => pendingSuppressionLists?.length,
    [pendingSuppressionLists]
  );
  useEffect(() => {
    setIsPendingListChanged(true);
  }, [numberOfPendingItems]);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<ContactList[]>([]);
  const debouncedSearchText = useDebounce(searchText, 750);

  // Refresh list
  const shouldRefreshList = useSelector(selectShouldRefreshList) || isPendingListChanged;
  useEffect(() => {
    if (shouldRefreshList) {
      refetchContactLists();
      refetchPendingContactLists();
      setIsPendingListChanged(false);
      dispatch(setShouldRefreshList(false));
    }
  }, [dispatch, refetchContactLists, refetchPendingContactLists, shouldRefreshList]);

  useEffect(() => {
    setIsSearchEmpty(searchText === '');
    if (!searchText || !debouncedSearchText || searchText !== debouncedSearchText) {
      return setSearchResults(suppressionLists ?? []);
    }

    if (debouncedSearchText) {
      setSearchResults(
        suppressionLists?.filter((item) =>
          item.list_name.toLowerCase().includes(debouncedSearchText)
        ) ?? []
      );
    } else {
      setSearchResults(suppressionLists ?? []);
    }
  }, [debouncedSearchText, suppressionLists, searchText]);

  const assignedContactListItems = useSelector(selectAssignedSuppressionLists(jobId));
  const assignedContactLists = useMemo(() => {
    return assignedContactListItems
      .map(
        (item) =>
          suppressionLists?.find(
            (cl) => cl.list_id === item.suppression_list_id
          ) as ContactList
      )
      .filter(Boolean)
      .map((contactList) => mapContactListToJobSuppressionList(contactList, true));
  }, [suppressionLists, assignedContactListItems]);

  const availableContactLists = useMemo(() => {
    const assignedContactListIds = assignedContactListItems.map(
      (list) => list.suppression_list_id
    );
    const standardLists = searchResults
      .filter((item) => !assignedContactListIds.includes(item.list_id))
      .filter(Boolean)
      .map((contactList) => mapContactListToJobSuppressionList(contactList, false));

    // Combine pending lists with standard available lists
    return [...(pendingSuppressionLists || []), ...standardLists];
  }, [assignedContactListItems, searchResults, pendingSuppressionLists]);

  const handleAssign = (
    item: JobSuppressionListDetails & { leads_remaining?: number }
  ) => {
    dispatch(
      setAssignSuppressionList({
        jobId,
        assignment: {
          suppression_list_id: item.suppression_list_id,
          create_results_phone_list: false,
        },
      })
    );
  };

  const handleUnassign = (
    item: JobSuppressionListDetails & { leads_remaining?: number }
  ) => {
    dispatch(
      setUnassignSuppressionList({
        jobId,
        suppressionListId: item.suppression_list_id,
      })
    );
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        searchInputRef={searchInputRef}
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={isSearchEmpty}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UserBlockIconSvg className="h-5 w-5" />}
            title="Suppression Lists"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Action button */}
            <div className="flex h-[84px] items-center justify-end">
              <button
                className="btn btn-primary btn-sm flex gap-1 pl-1.5"
                onClick={() => {
                  dispatch(
                    addOpenModal({
                      modalName: Modals.P2PUploadModal,
                      modalVariant: 'narrow',
                      modalParams: JSON.stringify({
                        type: 'suppression',
                        hideOtherListTypes: true,
                        identityId: identityId,
                      } as P2PUploadParams),
                      // %7B"type"%3A"p2pSms"%7D
                    })
                  );
                }}>
                <PlusIconSvg className="h-5 w-5" />
                New List
              </button>
            </div>

            {/* Search */}
            <div className="search-container mb-6">
              <InputContainer
                label=""
                className="input input-bordered flex !flex-row items-center justify-between gap-2"
                iconPrefix={<SearchIconSvg className="h-6 w-6 text-gray-400" />}
                iconSuffix={
                  !!searchText && (
                    <CloseIconSvg
                      className="hover:text-gray-default cursor-pointer text-gray-400"
                      onClick={() => {
                        setSearchText('');
                      }}
                    />
                  )
                }>
                <input
                  type="search"
                  name="search"
                  ref={searchInputRef}
                  onChange={(e) =>
                    setSearchText((e.currentTarget.value ?? '').toLowerCase())
                  }
                  value={searchText}
                  placeholder="Search..."
                  className="w-full text-sm"
                />
              </InputContainer>
            </div>

            {/* Assignment List */}
            <AssignmentList
              assignedItems={assignedContactLists}
              availableItems={availableContactLists}
              LineItemView={SuppressionListItemView}
              onAssign={handleAssign}
              onUnassign={handleUnassign}
              isLoading={isLoadingContactLists || isLoadingPendingContactLists}
              searchText={searchText}
              emptyMessage={
                <div className="flex flex-col gap-3">
                  <span>No Contact Lists found</span>
                  {searchText && (
                    <button
                      className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                      onClick={() => setSearchText('')}>
                      Reset search
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                    onClick={() =>
                      dispatch(
                        addOpenModal({
                          modalName: Modals.P2PUploadModal,
                          modalVariant: 'narrow',
                          modalParams: JSON.stringify({
                            type: 'suppression',
                            hideOtherListTypes: true,
                            identityId: identityId,
                          } as P2PUploadParams),
                        })
                      )
                    }>
                    Create New
                  </button>
                </div>
              }
              getItemKey={(item) =>
                item.suppression_list_id
                  ? item.suppression_list_id
                  : `pending-${item.suppression_list_name}`
              }
              assignButtonLabel="Assign"
              unassignButtonLabel="Unassign"
            />
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AssignSuppressionListsModal;
